// Pure, system-neutral helpers for associating canonical Core groups with
// project records and scene nodes. Product adapters are responsible for
// publishing stable `objectIds` and generic object/group metadata.

function normalizedText(value) {
  return String(value ?? '').trim();
}

function normalizeSystemId(value, normalizer = null) {
  const raw = normalizedText(value);
  if (!raw) return '';
  if (typeof normalizer === 'function') {
    const normalized = normalizedText(normalizer(raw));
    if (normalized) return normalized.toLowerCase();
  }
  return raw.toLowerCase();
}

function idsFromValues(values = []) {
  const ids = new Set();
  const add = value => {
    if (Array.isArray(value) || value instanceof Set) {
      value.forEach(add);
      return;
    }
    const id = normalizedText(value);
    if (id) ids.add(id);
  };
  add(values);
  return ids;
}

function normalizeCoreGroupKeys(values) {
  if (values == null) return null;
  const source = values instanceof Set ? [...values] : Array.isArray(values) ? values : [];
  return [...new Set(source.map(normalizedText).filter(Boolean))].sort();
}

function coreGroupSelectionSignature(values) {
  const keys = normalizeCoreGroupKeys(values);
  return keys === null ? '*' : keys.join('|');
}

function normalizeCoreGroupFilters(groups = [], options = {}) {
  return (Array.isArray(groups) ? groups : []).map(rawGroup => {
    if (!rawGroup || typeof rawGroup !== 'object') return null;
    const source = rawGroup.source || rawGroup;
    const systemId = normalizeSystemId(
      rawGroup.systemId || rawGroup.systemKey,
      options.normalizeSystemId
    );
    const explicitKey = normalizedText(rawGroup.key);
    const groupId = normalizedText(
      rawGroup.groupId
      || rawGroup.id
      || rawGroup.primaryId
      || (systemId && explicitKey.startsWith(`${systemId}:`)
        ? explicitKey.slice(systemId.length + 1)
        : '')
    );
    const key = systemId && groupId ? `${systemId}:${groupId}` : '';
    if (!systemId || !groupId || !key || (explicitKey && explicitKey !== key)) return null;
    return {
      key,
      groupId,
      systemId,
      objectIds: idsFromValues([
        groupId,
        rawGroup.id,
        rawGroup.primaryId,
        rawGroup.sourceId,
        rawGroup.objectId,
        rawGroup.objectIds,
        rawGroup.memberIds,
        rawGroup.renderId,
        rawGroup.renderIds
      ]),
      bounds: rawGroup.bounds || source?.bounds || null,
      source
    };
  }).filter(Boolean);
}

function selectedCoreGroupFilters(groups = [], selectedKeys = null, options = {}) {
  const normalizedGroups = normalizeCoreGroupFilters(groups, options);
  const keys = normalizeCoreGroupKeys(selectedKeys);
  if (keys === null) return normalizedGroups;
  const selected = new Set(keys);
  return normalizedGroups.filter(group => selected.has(group.key));
}

function idsForProjectObject(object = {}) {
  const genericIds = value => [
    value?.id,
    value?.refId,
    value?.objectId,
    value?.objectIds,
    value?.groupId,
    value?.groupIds,
    value?.sourceId,
    value?.sourceIds,
    value?.sourceObjectId,
    value?.sourceObjectIds,
    value?.sourceGroupId,
    value?.sourceGroupIds,
    value?.parentId,
    value?.parentObjectId,
    value?.parentObjectIds
  ];
  return idsFromValues([
    genericIds(object),
    genericIds(object.meta),
    genericIds(object.data),
    genericIds(object.metadata)
  ]);
}

function idsForSceneObject(object3d = {}) {
  const data = object3d?.userData || {};
  return idsFromValues([
    data.id,
    data.refId,
    data.objectId,
    data.objectIds,
    data.groupId,
    data.groupIds,
    data.ksGroupId,
    data.ksGroupIds,
    data.sourceId,
    data.sourceIds,
    data.sourceObjectId,
    data.sourceObjectIds,
    data.sourceGroupId,
    data.sourceGroupIds,
    data.parentId,
    data.parentObjectId,
    data.parentObjectIds
  ]);
}

function projectObjectSystemId(object = {}, options = {}) {
  return normalizeSystemId(
    options.ownerSystemId
      || object.systemId
      || object.systemKey
      || object.system
      || object.meta?.systemId
      || object.meta?.systemKey
      || object.data?.systemId
      || object.data?.systemKey
      || object.metadata?.systemId
      || object.metadata?.systemKey,
    options.normalizeSystemId
  );
}

function nodeDeclaredSystemId(object3d = {}, options = {}) {
  const data = object3d?.userData || {};
  return normalizeSystemId(
    data.systemId
      || data.systemKey
      || data.system
      || data.projectObject?.systemId
      || data.projectObject?.systemKey,
    options.normalizeSystemId
  );
}

function sceneSystemRootOwner(object3d, root = null, options = {}) {
  let current = object3d || null;
  let hostRootOwner = '';
  while (current) {
    if (current?.userData?.ksSystemRoot3D === true) {
      // Der Hostroot ist der aeusserste markierte Knoten innerhalb des
      // uebergebenen Projektroots. Innere Marker sind Plugininhalt.
      hostRootOwner = nodeDeclaredSystemId(current, options);
    }
    if (current === root) break;
    current = current.parent || null;
  }
  return hostRootOwner;
}

function sceneSystemOwner(object3d, root = null, options = {}) {
  const suppliedOwner = normalizeSystemId(options.ownerSystemId, options.normalizeSystemId);
  if (suppliedOwner) return suppliedOwner;

  const hostRootOwner = sceneSystemRootOwner(object3d, root, options);
  if (hostRootOwner) return hostRootOwner;

  let current = object3d || null;
  while (current) {
    const owner = nodeDeclaredSystemId(current, options);
    if (owner) return owner;
    if (current === root) break;
    current = current.parent || null;
  }
  return '';
}

function groupContainsIds(group, ids, ownerSystemId = '', options = {}) {
  const owner = normalizeSystemId(ownerSystemId, options.normalizeSystemId);
  const groupOwner = normalizeSystemId(group?.systemId, options.normalizeSystemId);
  if (!owner || !groupOwner || owner !== groupOwner) return false;
  for (const id of ids || []) {
    if (group?.objectIds?.has?.(id)) return true;
  }
  return false;
}

function explicitSceneGroupKeys(object3d = {}, groups = [], options = {}) {
  const owner = sceneSystemRootOwner(object3d, options.root || null, options);
  if (!owner) return [];
  const suppliedOwner = normalizeSystemId(options.ownerSystemId, options.normalizeSystemId);
  if (suppliedOwner && suppliedOwner !== owner) return [];
  const validKeys = new Set(groups
    .filter(group => normalizeSystemId(group?.systemId, options.normalizeSystemId) === owner)
    .map(group => group.key));
  const data = object3d?.userData || {};
  const rawKeys = Array.isArray(data.ksGroupKeys)
    ? data.ksGroupKeys
    : data.ksGroupKey
      ? [data.ksGroupKey]
      : [];
  return [...new Set(rawKeys.map(normalizedText).filter(key => validKeys.has(key)))];
}

function groupKeysForProjectObject(object = {}, groups = [], options = {}) {
  const owner = projectObjectSystemId(object, options);
  const ids = idsForProjectObject(object);
  return groups
    .filter(group => groupContainsIds(group, ids, owner, options))
    .map(group => group.key);
}

function groupKeysForSceneObject(object3d = {}, groups = [], options = {}) {
  const owner = sceneSystemOwner(object3d, options.root || null, options);
  const ids = idsForSceneObject(object3d);
  return groups
    .filter(group => groupContainsIds(group, ids, owner, options))
    .map(group => group.key);
}

function sceneNodeChain(object3d, root = null) {
  const chain = [];
  let current = object3d || null;
  while (current) {
    chain.push(current);
    if (current === root) break;
    current = current.parent || null;
  }
  return chain;
}

function sceneNodeDeclaresExplicitGroupKey(object3d, root = null) {
  return sceneNodeChain(object3d, root).some(node => {
    if (node?.userData?.ksSystemRoot3D === true) return false;
    const data = node?.userData || {};
    return normalizedText(data.ksGroupKey)
      || (Array.isArray(data.ksGroupKeys) && data.ksGroupKeys.some(normalizedText));
  });
}

function sceneObjectDeclaresExplicitGroupKey(object3d) {
  const data = object3d?.userData || {};
  return Boolean(
    normalizedText(data.ksGroupKey)
    || (Array.isArray(data.ksGroupKeys) && data.ksGroupKeys.some(normalizedText))
  );
}

function groupKeysForSceneNode(object3d, groups = [], root = null, options = {}) {
  if (!object3d) return [];
  const ownerSystemId = sceneSystemOwner(object3d, root, options);
  if (!ownerSystemId) return [];
  const scopedOptions = { ...options, ownerSystemId, root };
  const chain = sceneNodeChain(object3d, root);

  // A group marker on the nearest parent scopes its complete subtree. The
  // node's own marker is considered next, and every explicit key is checked
  // against the authoritative system root before it is accepted.
  for (const current of [...chain.slice(1), chain[0]]) {
    if (current?.userData?.ksSystemRoot3D === true) continue;
    if (!sceneObjectDeclaresExplicitGroupKey(current)) continue;
    return explicitSceneGroupKeys(current, groups, scopedOptions);
  }

  for (const current of chain) {
    if (current?.userData?.ksSystemRoot3D === true) continue;
    const projectObject = current?.userData?.projectObject;
    if (projectObject) {
      const projectKeys = groupKeysForProjectObject(projectObject, groups, scopedOptions);
      if (projectKeys.length) return projectKeys;
    }
    const sceneKeys = groupKeysForSceneObject(current, groups, scopedOptions);
    if (sceneKeys.length) return sceneKeys;
  }
  return [];
}

function selectedCoreGroupKeysVisible(groupKeys = [], selectedKeys = null) {
  const selection = normalizeCoreGroupKeys(selectedKeys);
  if (!groupKeys.length || selection === null) return true;
  const selected = new Set(selection);
  return groupKeys.some(key => selected.has(key));
}

function coreGroupVisibilityAllowsKeys(groupKeys = [], visibility = null) {
  if (!groupKeys.length) return true;
  if (visibility instanceof Map) return groupKeys.some(key => visibility.get(key) !== false);
  return groupKeys.some(key => visibility?.[key] !== false);
}

function coreSystemObjectSelected(groups = [], selectedKeys = null, systemId = '', objectId = '', options = {}) {
  const id = normalizedText(objectId);
  if (!id) return true;
  const normalizedGroups = normalizeCoreGroupFilters(groups, options);
  const selection = normalizeCoreGroupKeys(selectedKeys);
  if (selection === null) return true;
  const selected = new Set(selection);
  const owner = normalizeSystemId(systemId, options.normalizeSystemId);
  return normalizedGroups.some(group => (
    selected.has(group.key)
    && normalizeSystemId(group.systemId, options.normalizeSystemId) === owner
    && group.objectIds.has(id)
  ));
}

function selectedCoreGroupBounds(groups = [], selectedKeys = null, options = {}) {
  const normalizedGroups = normalizeCoreGroupFilters(groups, options);
  const selection = normalizeCoreGroupKeys(selectedKeys);
  const selected = selection === null ? null : new Set(selection);
  return normalizedGroups.reduce((result, group) => {
    if (selected && !selected.has(group.key)) return result;
    const source = group.bounds || group.source?.bounds;
    const minX = Number(source?.minX);
    const minZ = Number(source?.minZ);
    const maxX = Number(source?.maxX);
    const maxZ = Number(source?.maxZ);
    if (![minX, minZ, maxX, maxZ].every(Number.isFinite)) return result;
    if (!result) return { minX, minZ, maxX, maxZ };
    return {
      minX: Math.min(result.minX, minX),
      minZ: Math.min(result.minZ, minZ),
      maxX: Math.max(result.maxX, maxX),
      maxZ: Math.max(result.maxZ, maxZ)
    };
  }, null);
}

function applySceneCoreGroupVisibility(root, groups = [], selectedKeys = null, options = {}) {
  const normalizedGroups = normalizeCoreGroupFilters(groups, options);
  const selection = normalizeCoreGroupKeys(selectedKeys);
  const previousVisibility = new Map();
  let restored = false;
  if (!root?.traverse || selection === null) {
    return {
      groups: normalizedGroups,
      selectedGroupKeys: selection,
      restore() { /* no visibility was changed */ }
    };
  }
  const selected = new Set(selection);
  const groupsByOwner = new Map();
  normalizedGroups.forEach(group => {
    if (!groupsByOwner.has(group.systemId)) groupsByOwner.set(group.systemId, []);
    groupsByOwner.get(group.systemId).push(group);
  });
  const nodes = [];
  root.traverse(object3d => {
    if (object3d && object3d !== root) nodes.push(object3d);
  });
  const filteredVisibility = new Map();
  [...nodes].reverse().forEach(object3d => {
    const hostOwner = sceneSystemRootOwner(object3d, root, options);
    const owner = hostOwner || sceneSystemOwner(object3d, root, options);
    const keys = groupKeysForSceneNode(object3d, normalizedGroups, root, options);
    const declaresExplicitKey = sceneNodeDeclaresExplicitGroupKey(object3d, root);
    if (!hostOwner && !keys.length && !declaresExplicitKey) return;
    const ownerGroups = groupsByOwner.get(owner) || [];
    if (!ownerGroups.length && !declaresExplicitKey) return;
    const ownerFilterActive = ownerGroups.some(group => !selected.has(group.key));
    let allowed = true;
    if (keys.length) {
      allowed = selectedCoreGroupKeysVisible(keys, selection);
    } else if (declaresExplicitKey) {
      allowed = false;
    } else if (ownerFilterActive) {
      const selectedChildVisible = (object3d.children || [])
        .some(child => filteredVisibility.get(child) === true);
      allowed = !isRenderableSceneNode(object3d) && selectedChildVisible;
    }
    previousVisibility.set(object3d, object3d.visible);
    const persistentState = options.visibilityState?.get?.(object3d);
    const baseVisible = persistentState?.filtered === true
      ? persistentState.baseVisible !== false
      : object3d.visible !== false;
    const nodeFilterActive = ownerFilterActive || (declaresExplicitKey && !keys.length);
    options.visibilityState?.set?.(object3d, {
      baseVisible,
      filtered: nodeFilterActive
    });
    object3d.visible = baseVisible && allowed;
    filteredVisibility.set(object3d, object3d.visible !== false);
  });
  return {
    groups: normalizedGroups,
    selectedGroupKeys: selection,
    restore() {
      if (restored) return;
      restored = true;
      previousVisibility.forEach((visible, object3d) => {
        object3d.visible = visible;
      });
      previousVisibility.clear();
    }
  };
}

function isRenderableSceneNode(object3d = {}) {
  return object3d.isMesh === true
    || object3d.isLine === true
    || object3d.isLineSegments === true
    || object3d.isPoints === true
    || object3d.isSprite === true
    || ['mesh', 'line', 'linesegments', 'points', 'sprite']
      .includes(normalizedText(object3d.type).toLowerCase());
}

export {
  applySceneCoreGroupVisibility,
  coreGroupSelectionSignature,
  coreGroupVisibilityAllowsKeys,
  coreSystemObjectSelected,
  explicitSceneGroupKeys,
  groupKeysForProjectObject,
  groupKeysForSceneNode,
  groupKeysForSceneObject,
  idsForProjectObject,
  idsForSceneObject,
  normalizeCoreGroupFilters,
  normalizeCoreGroupKeys,
  sceneNodeDeclaresExplicitGroupKey,
  sceneSystemRootOwner,
  selectedCoreGroupBounds,
  selectedCoreGroupFilters,
  selectedCoreGroupKeysVisible
};
